<template>
  <div>
    <section class="mb-5">
      <CCard>
        <CCardBody>
          <ProductsTableListFilters @onSubmit="onFilterChange" />
          <div>
            <CRow>
              <CCol>
                <CDropdown
                  :togglerText="$t('producer.products.editBtn.title')"
                  color="primary"
                  :disabled="actionsDisabled"
                >
                  <CDropdownItem @click="activeSelected()">{{
                    $t("producer.products.editBtn.enable")
                  }}</CDropdownItem>
                  <CDropdownItem @click="deactiveSelected()">{{
                    $t("producer.products.editBtn.disable")
                  }}</CDropdownItem>
                  <CDropdownDivider />
                  <CDropdownItem @click="editPriceSelected()">{{
                    $t("producer.products.editBtn.price")
                  }}</CDropdownItem>
                  <CDropdownItem @click="editAvailabilitySelected()">{{
                    $t("producer.products.editBtn.availability")
                  }}</CDropdownItem>
                </CDropdown>
              </CCol>
            </CRow>
            <CRow>
              <table class="table m-3">
                <thead>
                  <tr>
                    <th style="width: 3%; min-width: 30px; text-align: center">
                      <div class="table-checkbox custom-checkbox">
                        <input
                          type="checkbox"
                          class="table-checkbox-input"
                          id="selectAll"
                          @change="selectAll"
                        />
                        <label
                          class="table-checkbox-label"
                          for="selectAll"
                        ></label>
                      </div>
                    </th>
                    <th style="width: 10%"></th>

                    <SortableHeader
                      :styles="'width: 20%'"
                      :param="'name'"
                      :ordering="ordering"
                      @sort="sortBy"
                    >
                      {{ $t("models.productproducer.name") }}
                    </SortableHeader>

                    <SortableHeader
                      :styles="'width: 25%'"
                      :param="'category_name'"
                      :ordering="ordering"
                      @sort="sortBy"
                    >
                      {{ $t("models.productproducer.category") }}
                    </SortableHeader>

                    <SortableHeader
                      :styles="'width: 15%'"
                      :param="'price'"
                      :ordering="ordering"
                      @sort="sortBy"
                    >
                      {{ $t("models.productproducer.price_l") }}
                    </SortableHeader>

                    <th style="width: 10%">
                      {{ $t("models.productproducer.markets") }}
                    </th>
                    <th style="width: 10%">
                      {{ $t("models.productproducer.actions.duplicate") }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <template v-if="loading">
                    <tr v-for="n in 12" :key="n">
                      <td
                        style="width: 3%; min-width: 30px; text-align: center"
                      >
                        <div class="table-checkbox custom-checkbox">
                          <input
                            type="checkbox"
                            class="table-checkbox-input"
                            id="sel"
                            disabled
                          />
                          <label class="table-checkbox-label" for="sel"></label>
                        </div>
                      </td>
                      <td style="width: 10%">
                        <SkeletonCell width="50%" />
                      </td>
                      <td style="width: 20%"><SkeletonCell width="100%" /></td>
                      <td style="width: 25%"><SkeletonCell width="100%" /></td>
                      <td style="width: 15%"><SkeletonCell width="100%" /></td>
                      <td style="width: 10%"><SkeletonCell width="100%" /></td>
                      <td style="width: 10%"><SkeletonCell width="30%" /></td>
                    </tr>
                  </template>
                  <template v-else>
                    <tr
                      v-for="(item, index) in products"
                      :key="index"
                      :class="{ selected: item.selected }"
                    >
                      <td style="text-align: center">
                        <div class="table-checkbox custom-checkbox">
                          <input
                            type="checkbox"
                            class="table-checkbox-input"
                            :id="'checkbox-' + index"
                            v-model="item.selected"
                            @change="onCheckboxChange"
                          />
                          <label
                            class="table-checkbox-label"
                            :for="'checkbox-' + index"
                          ></label>
                        </div>
                      </td>
                      <td>
                        <img
                          :src="
                            item.image
                              ? item.image.image_thumb
                              : item.productproducer.image
                              ? item.productproducer.image.image_thumb
                              : '/loonity/img/placeholder.svg'
                          "
                          height="50"
                          class="border rounded"
                        />
                      </td>
                      <td>
                        <strong @click="editClick(item)">{{
                          item.productproducer.translations[lang].name
                        }}</strong
                        ><br />
                        {{ item.translations[lang].name }}
                      </td>
                      <td>
                        <div>
                          <div
                            class="ui-type-caption text-gray-600 text-uppercase"
                          >
                            {{
                              item.productproducer.category.parent.translations[
                                lang
                              ].name
                            }}
                          </div>
                          {{
                            item.productproducer.category.translations[lang]
                              .name
                          }}
                        </div>
                      </td>
                      <td>
                        <strong>{{ item.price.toFixed(2) }} CHF</strong>
                      </td>
                      <td>
                        <div
                          v-if="
                            item.is_active && item.productproducer.is_active
                          "
                        >
                          <CBadge
                            v-if="
                              !item.is_active && item.productproducer.is_active
                            "
                            shape="pill"
                            color="gray-200"
                            >{{
                              $t("producer.products.table.onsale.disabled")
                            }}</CBadge
                          >

                          <div
                            v-else
                            class="d-inline-block text-start"
                            v-c-tooltip="{
                              html: true,
                              content: getMarketNamesHtml(item.ppls),
                              placement: 'left',
                            }"
                          >
                            <span
                              v-for="market in item.ppls"
                              class="badge badge-bubble badge-pill"
                              :class="{
                                'badge-success': market.active,
                              }"
                              :key="market.id"
                            ></span>
                          </div>
                        </div>
                        <div v-else>
                          <CBadge shape="pill" color="gray-200">{{
                            $t("producer.products.table.onsale.disabled")
                          }}</CBadge>
                        </div>
                      </td>
                      <td>
                        <button
                          class="btn btn-outline-primary"
                          title="Duplicate"
                          @click="duplicateClick(item)"
                        >
                          <font-awesome-icon :icon="['fas', 'copy']" />
                        </button>
                      </td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </CRow>
            <CRow>
              <CCol>
                <Pagination
                  :data="pagination"
                  @paginate="fetchData"
                  :maxVisiblePages="7"
                />
              </CCol>
              <CCol xs="2" sm="2" md="1" lg="1" class="ml-auto">
                <UiSelect
                  :value.sync="queryFilters.limit.term"
                  :options="[12, 50, 100, 200]"
                  custom
                />
              </CCol>
            </CRow>
          </div>
        </CCardBody>
      </CCard>
    </section>
  </div>
</template>

<script>
import UiSelect from "../../../common/form/UiSelect";
import ProductsTableListFilters from "./ProductsTableListFilters";
import { apiGetRQL } from "../../../helpers/apiHelper";
import Pagination from "../../../common/table/Pagination.vue";
import SkeletonCell from "../../../common/table/Skeleton.vue";
import { endPoint } from "../../../ds";
import { scrollToTopOfpage } from "../../../helpers/common";
import SortableHeader from "../../../common/table/SortableHeaderElement.vue";

export default {
  name: "ProductsTableList",

  components: {
    SortableHeader,
    UiSelect,
    ProductsTableListFilters,
    Pagination,
    SkeletonCell,
  },

  data: function () {
    return {
      loading: true,
      queryFilters: {
        limit: {
          style: "query",
          operator: "=",
          term: parseInt(localStorage.getItem("l_prod_rows")) || 12,
        },
        offset: {
          style: "query",
          operator: "=",
          term: 0,
        },
      },
      ordering: {
        style: "sort",
        term: null,
      },
      products: [],
      pagination: {
        count: 0,
        next: null,
        previous: null,
        limit: parseInt(localStorage.getItem("l_prod_rows")) || 12,
      },
      actionsDisabled: true,
      intendedPageSize: parseInt(localStorage.getItem("l_prod_rows")) || 12,
      pageSettings: {
        pageSize: parseInt(localStorage.getItem("l_prod_rows")) || 12,
      },
    };
  },

  computed: {
    lang: function () {
      return this.$i18n.locale;
    },
  },

  methods: {
    getMarketNamesHtml(ppls) {
      if (!ppls || ppls.length === 0) return "";

      const marketNames = ppls
        .filter((p) => p.market?.role?.name)
        .map((p) => p.market.role.name);

      if (marketNames.length === 0) return "";

      return (
        '<ul style="text-align: left; margin:0 0 0 20px;padding:0;"><li>' +
        marketNames.join("</li><li>") +
        "</li></ul>"
      );
    },

    duplicateClick(item) {
      this.$router.push({
        name: "RoleProductsCreate",
        params: { id: this.$store.state.role.id },
        query: { from_product_id: item.productproducer.id },
      });
    },
    editClick(item) {
      this.$router.push({
        name: "RoleProductsEdit",
        params: {
          id: this.$store.state.role.id,
          product_id: item.productproducer.id,
        },
      });
    },
    selectAll(event) {
      this.products = this.products.map((product) => {
        product.selected = event.target.checked;
        return product;
      });
      this.onCheckboxChange();
    },

    getSelectedRows() {
      return this.products.filter((product) => product.selected);
    },

    onCheckboxChange() {
      const selected = this.getSelectedRows();
      this.actionsDisabled = selected.length === 0;
    },

    rowDataBound(args) {
      if (args.data["is_active"] !== true) {
        args.row.classList.add("ui-row-disabled");
      }
    },

    activeSelected() {
      const selected = this.getSelectedRows();
      if (selected.length > 0) {
        this.changeIsActive(selected, true);
      }
    },

    deactiveSelected() {
      const selected = this.getSelectedRows();
      if (selected.length > 0) {
        this.changeIsActive(selected, false);
      }
    },

    editPriceSelected() {
      const selected = this.getSelectedRows();
      if (selected.length > 0) {
        this.$store.commit("service/selectedVariants", selected);
        this.$router.push({
          name: "RoleProductsEditPrices",
          params: {
            id: this.$store.state.role.id,
          },
        });
      }
    },

    editAvailabilitySelected() {
      const selected = this.getSelectedRows();
      if (selected.length > 0) {
        this.$store.commit("service/selectedVariants", selected);
        this.$router.push({
          name: "RoleProductsEditAvailability",
          params: {
            id: this.$store.state.role.id,
          },
        });
      }
    },

    sortBy(param) {
      if (this.ordering.term === param) {
        this.ordering.term = "-" + param;
      } else {
        this.ordering.term = param;
      }
      this.fetchData();
    },

    async onFilterChange(formData) {
      const lang = this.$i18n.locale;
      let newQueryFilters = this.queryFilters;
      if (formData.category_id) {
        newQueryFilters["category"] = {
          style: "function",
          term: formData.category_id,
          operator: "eq",
        };
      } else {
        this.$delete(this.queryFilters, "category");
      }
      if (formData.logistic_type_id) {
        newQueryFilters["type_product"] = {
          style: "function",
          term: formData.logistic_type_id,
          operator: "eq",
        };
      } else {
        this.$delete(this.queryFilters, "type_product");
      }
      if (formData.sell_id == "true") {
        // non attivo
        newQueryFilters["active_variant"] = {
          style: "query",
          term: formData.sell_id,
          operator: "=",
        };
      }
      if (formData.sell_id == "false") {
        // in vendita
        newQueryFilters["active_variant"] = {
          style: "query",
          term: formData.sell_id,
          operator: "=",
        };
      }
      if (formData.sell_id == "") {
        this.$delete(this.queryFilters, "active_variant");
      }
      if (formData.q) {
        newQueryFilters["search"] = {
          style: "query",
          term: formData.q,
          operator: "=",
        };
      } else {
        this.$delete(this.queryFilters, "search");
      }

      this.queryFilters = newQueryFilters;
      await this.fetchData();
    },

    changeIsActive(selected, status) {
      const self = this;
      const roleId = this.$store.state.role.id;
      const url =
        endPoint("role_producer_products_lots_NEW", [roleId]) + "multi_update/";

      const params = selected.map((row) => {
        return { id: row.id, is_active: status };
      });

      const handleRequestErrors = (error) => {
        console.error(error.response.data);
      };

      this.axios
        .patch(url, params)
        .then(() => {
          self.$store.dispatch("toaster/add", {
            title: this.$t("role.product.toast.activating.title"),
            text: `${this.$t("role.product.toast.activating.products")} ${
              status
                ? this.$t("role.product.toast.activating.activated")
                : this.$t("role.product.toast.activating.deactivated")
            } ${this.$t("role.product.toast.activating.success")}`,
            autohide: true,
            color: "success",
          });

          this.fetchData();
        })
        .catch(handleRequestErrors);
    },

    async fetchData(page = null) {
      this.loading = true;

      let filter = this.queryFilters;

      if (page) {
        filter.offset = {
          style: "query",
          operator: "=",
          term: (page - 1) * this.queryFilters.limit.term,
        };
      }

      filter.limit = {
        style: "query",
        operator: "=",
        term: this.queryFilters.limit.term,
      };

      if (this.ordering.term) {
        filter.ordering = this.ordering;
      }

      let params = {
        endpoint: "role_producer_products_lots_NEW",
        params: this.$store.state.role.id,
        filter: filter,
      };

      const response = await apiGetRQL(params);

      if (response && response.data) {
        this.products = response.data.payload.Items;
        this.pagination = {
          count: response.data.payload.Count,
          limit: parseInt(filter.limit.term),
          offset: parseInt(filter.offset.term),
        };
        this.loading = false;
        scrollToTopOfpage(this);
      }
    },
  },
  watch: {
    "queryFilters.limit.term": async function (newVal, oldVal) {
      localStorage.setItem("l_prod_rows", parseInt(newVal));
      this.pagination.limit = parseInt(newVal);
      await this.fetchData();
    },
  },
  async created() {
    await this.fetchData();
  },
};
</script>
